import React, {useState} from "react";
import {Grid, TextField, Button, MenuItem, IconButton, Autocomplete} from "@mui/material";
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';

import {AuctionType, PlanItemState, PlanState, ProductType} from "../../../../utils/enums";
import {filterInitialState, PlanFilterParams, SearchFunction} from "../../../../types/plan";
import {useDictionary} from "../../../../utils/useDictionary";
import ru from 'date-fns/locale/ru';
import kk from 'date-fns/locale/kk';

const FilterPanel = ({t, onSearch, i18n, initialState}: { t: any, onSearch: SearchFunction<PlanFilterParams>, i18n: any, initialState: PlanFilterParams }) => {

    const [filters, setFilters] = useState<PlanFilterParams>(initialState);

    const handleInputChange = (field: keyof PlanFilterParams, value: any) => {
        setFilters(prev => ({...prev, [field]: value}));
    };

    const handleSearch = () => {
        onSearch({
            ...filters,
            state: (planItemState?.selected?.value || null) as PlanItemState,
            auctionType: (auctionType?.selected?.value || null) as AuctionType,
            productType: (productType?.selected?.value || null) as ProductType
        });
    };

    const handleClear = () => {
        setFilters(initialState);
        auctionType.setSelected({'value': '', 'label': ''})
        planItemState.setSelected({'value': '', 'label': ''})
        productType.setSelected({'value': '', 'label': ''})
        onSearch({...filterInitialState, auctionType: null, state: null, productType: null});
    };

    const auctionType = useDictionary(
        AuctionType,
        (type) => ({value: type, label: t(`auction_type.${type}`)}),
        {value: '', label: ''}
    );

    const planItemState = useDictionary(
        PlanItemState,
        (type) => ({value: type, label: t(`plan_item_state.${type}`)}),
        {value: '', label: ''}
    );

    const productType = useDictionary(
        ProductType,
        (type) => ({value: type, label: t(`product_type.${type}`)}),
        {value: '', label: ''}
    );

    const emptyOption = {value: "", label: ""};
    const auctionTypeOptions = [emptyOption, ...auctionType.dictionary];
    const planItemStateOptions = [emptyOption, ...planItemState.dictionary];
    const productTypeOptions = [emptyOption, ...productType.dictionary];

    const locale = i18n.language === "ru" ? ru : kk;

    return (
        <Grid container marginBottom={2} spacing={2}>
            {/* Поле "name" */}
            <Grid item xs={12} sm={6} md={3}>
                <TextField
                    label="Название"
                    fullWidth
                    value={filters.name || ''}
                    size={"small"}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                />
            </Grid>

            {/* Поле "auctionType" */}
            <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                    disablePortal
                    options={auctionTypeOptions}
                    value={auctionType.selected}
                    onChange={auctionType.onSelectChange}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    fullWidth
                    size={"small"}
                    renderInput={params =>
                        <TextField {...params} fullWidth color="primary"
                                   name="auctionType" label={t("cabinet.plan.filter.auctionType")}
                        />
                    }
                />
            </Grid>

            {/* Поле "itemType" */}
            <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                    disablePortal
                    options={productTypeOptions}
                    value={productType.selected}
                    onChange={productType.onSelectChange}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    fullWidth
                    size="small"
                    renderInput={params =>
                        <TextField {...params} fullWidth color="primary"
                                   name="productType" label={t("cabinet.plan.filter.productType")}
                        />
                    }
                />
            </Grid>

            {/* Поле "state" */}
            <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                    disablePortal
                    options={planItemStateOptions}
                    value={planItemState.selected}
                    onChange={planItemState.onSelectChange}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    fullWidth
                    size="small"
                    renderInput={params =>
                        <TextField {...params} fullWidth color="primary"
                                   name="planItemState" label={t("cabinet.plan.filter.planItemState")}
                        />
                    }
                />
            </Grid>

            {/* Поле "auctionDate" */}
            <Grid item xs={12} sm={6} md={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale} size="small">
                    <DatePicker
                        // Указываем, что доступны только представления месяца и дня
                        views={['month', 'year']}
                        // Формат отображения без года
                        inputFormat="MM.yyyy"
                        label={t("cabinet.plan.filter.auctionDate")}
                        value={filters.auctionDate}
                        onChange={(newValue) => {
                            handleInputChange('auctionDate', newValue);
                        }}
                        renderInput={(params) => <TextField {...params} InputLabelProps={{shrink: true}}/>}
                    />
                </LocalizationProvider>
            </Grid>

            {/* Кнопка поиска */}
            <Grid container margin={2} spacing={2}>

                <Grid item xs={12} sm={6} md={3}>
                    <Button variant="contained" color="primary" fullWidth onClick={handleSearch}>
                        {t("common.search")}
                    </Button>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Button variant="contained" color="secondary" fullWidth onClick={handleClear}>
                        {t("common.clear")}
                    </Button>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default FilterPanel;